import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui.jsx";
import { api } from "../lib/api.js";
import { fmtMinor } from "../lib/money.js";
import { ChecklistRows, checklistFromGate, COMPANY_DETAILS_PATH, BILLING_PATH } from "../lib/onboarding.jsx";

// Shown when an action is refused by a deploy gate: HTTP 402 (billing) or HTTP 403
// `onboarding_required` (company details / verification). Maps the server `code` to the exact
// action that clears it: finish account setup, assign a plan, start the subscription, or top
// up credit. Stripe actions open Checkout/portal in a new tab, then close the modal.
//
// gate: { code, message, missing?, emailVerified?, phoneVerified?, requiredPence?, balancePence? }
// items: optional checklist rows to show for onboarding_required (the shell passes the full
//        live checklist; without it the rows are rebuilt from the refusal itself).
export default function BillingGateModal({ gate, onClose, onGoToSettings, items }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const openCheckout = async (fn) => {
    setBusy(true); setErr(null);
    try {
      const { url } = await fn();
      if (url) window.open(url, "_blank", "noopener");
      onClose();
    } catch (e) { setErr(e?.message || "Something went wrong"); setBusy(false); }
  };
  const go = (to) => { onClose(); navigate(to); };

  const need = Number(gate.requiredPence);
  const have = Number(gate.balancePence);
  const onboardingRows = items?.length ? items : checklistFromGate(gate);
  const onlyBilling = onboardingRows.length > 0 && onboardingRows.filter((r) => !r.done).every((r) => r.key === "billing");

  const config = {
    onboarding_required: {
      title: "Finish setting up your account to deploy",
      body: "We need your company details and a verified email address and phone number before anything can be deployed. Nothing has been started.",
      extra: <ChecklistRows items={onboardingRows} onNavigate={onClose} />,
      actions: [onlyBilling
        ? { label: "Set up billing", primary: true, onClick: () => go(BILLING_PATH) }
        : { label: "Complete company details", primary: true, onClick: () => go(COMPANY_DETAILS_PATH) }],
    },
    insufficient_credit: {
      title: "Add credit to continue",
      body: Number.isFinite(need) && need > 0
        ? `This needs ${fmtMinor(need)} of account credit${Number.isFinite(have) ? ` and your balance is ${fmtMinor(have)}` : ""}. Top up your wallet, then try again — nothing has been started.`
        : "There isn't enough account credit to do that. Top up your wallet, then try again — nothing has been started.",
      actions: [{ label: "Top up credit", primary: true, onClick: () => go(BILLING_PATH) }],
    },
    plan_required: {
      title: "Choose a plan first",
      body: "This service has no plan assigned, so it can't be billed or deployed. Pick a plan under Settings → Scale, then deploy.",
      actions: [{ label: "Go to plan settings", primary: true, onClick: () => { onClose(); onGoToSettings?.(); } }],
    },
    billing_setup_required: {
      title: "Set up billing to deploy",
      body: "Start a monthly card subscription for your assigned plans. Account credit is separate: add a wallet top-up if you need prepaid credit for usage.",
      actions: [
        { label: "Start subscription", primary: true, onClick: () => openCheckout(api.startMySubscription) },
        { label: "Add £25 credit", onClick: () => openCheckout(() => api.topup(2500)) },
      ],
    },
    account_suspended: {
      title: "Account suspended",
      body: "This account is suspended for non-payment. Settle the balance to resume deploys.",
      actions: [
        { label: "Add £25 credit", primary: true, onClick: () => openCheckout(() => api.topup(2500)) },
        { label: "Billing portal", onClick: () => openCheckout(api.billingPortal) },
      ],
    },
  };
  const c = config[gate.code] || { title: "Billing setup required", body: gate.message, actions: [] };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label={c.title} onClick={(e) => e.stopPropagation()} style={{ width: "min(470px, 94vw)", maxHeight: "90vh", overflowY: "auto", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 22, boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: "var(--text)" }}>{c.title}</h3>
        <p style={{ fontSize: 13, lineHeight: 1.5, color: "var(--text-muted)", marginTop: 8 }}>{c.body}</p>
        {c.extra && <div style={{ marginTop: 14 }}>{c.extra}</div>}
        {err && <p style={{ fontSize: 12.5, color: "var(--err-text)", marginTop: 10 }}>{err}</p>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Close</Button>
          {c.actions.map((a) => (
            <Button key={a.label} variant={a.primary ? "primary" : "secondary"} disabled={busy} onClick={a.onClick}>
              {busy && a.primary ? "Opening…" : a.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
